import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getCatalogStructure, getProgressSummary } from '../api';
import {
  findGradeLevel,
  formatGoalMarker,
  getGoalSections,
  getMajorTopics,
  getSkills,
} from '../utils/catalog';
import {
  buildStandardStats,
  skillProgress,
  skillStatusLabel,
  skillStatusTone,
  summariseSkills,
} from '../utils/progress';
import { saveRecentActivity } from '../utils/recentActivity';

function MajorTopicPage({ onStartSkill, authEmail, authPassword }) {
  const { stateName, gradeName, majorTopicName } = useParams();
  const navigate = useNavigate();
  const decodedState = decodeURIComponent(stateName || '');
  const decodedGrade = decodeURIComponent(gradeName || '');
  const decodedMajorTopic = decodeURIComponent(majorTopicName || '');

  const [catalog, setCatalog] = useState(null);
  const [standardStats, setStandardStats] = useState(() => new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const summaryPromise = authEmail && authPassword
      ? getProgressSummary(authEmail, authPassword, {
          state: decodedState,
          gradeLevel: decodedGrade,
        }).catch(() => ({ summary: [] }))
      : Promise.resolve({ summary: [] });

    Promise.all([getCatalogStructure(), summaryPromise])
      .then(([data, summaryData]) => {
        if (cancelled) return;
        setCatalog(data);
        setStandardStats(buildStandardStats(summaryData.summary, {
          state: decodedState,
          grade: decodedGrade,
        }));
        setLoading(false);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || 'Failed to load skills');
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [authEmail, authPassword, decodedState, decodedGrade]);

  const gradePath =
    `/state/${encodeURIComponent(decodedState)}/grade/${encodeURIComponent(decodedGrade)}`;
  const topicPath = `${gradePath}/topic/${encodeURIComponent(decodedMajorTopic)}`;

  useEffect(() => {
    if (!decodedMajorTopic) return;
    saveRecentActivity({
      state: decodedState,
      grade: decodedGrade,
      majorTopic: decodedMajorTopic,
      path: topicPath,
    });
  }, [decodedState, decodedGrade, decodedMajorTopic, topicPath]);

  const majorTopic = useMemo(() => {
    const gradeLevel = findGradeLevel(catalog, decodedState, decodedGrade);
    return getMajorTopics(gradeLevel).find((mt) => mt.name === decodedMajorTopic) || null;
  }, [catalog, decodedState, decodedGrade, decodedMajorTopic]);

  const summary = useMemo(() => {
    if (!majorTopic) return { total: 0, completed: 0, started: 0, percentage: 0 };
    return summariseSkills(getSkills(majorTopic), standardStats);
  }, [majorTopic, standardStats]);

  const nextSkill = useMemo(() => {
    if (!majorTopic) return null;
    return getSkills(majorTopic).find(
      (skill) => !skillProgress(skill, standardStats).completed
    ) || null;
  }, [majorTopic, standardStats]);

  const startSkill = (skill) => {
    if (!(skill.questionCount > 0)) return;
    const skillName = skill.name || skill.subTopicName;
    saveRecentActivity({
      state: decodedState,
      grade: decodedGrade,
      majorTopic: decodedMajorTopic,
      skill: skillName,
      path: topicPath,
    });
    onStartSkill({
      state: decodedState,
      gradeLevel: decodedGrade,
      standardCode: skill.code || undefined,
      subTopic: skill.code ? undefined : skill.subTopicName,
    }, skillName);
  };

  return (
    <div className="browse-page-container">
      <button type="button" className="back-button" onClick={() => navigate(gradePath)}>
        ← Back to {decodedGrade || 'learning path'}
      </button>

      <div className="browse-page-header">
        <h1 className="browse-page-title">{decodedMajorTopic}</h1>
        <p className="browse-page-subtitle">
          {decodedGrade ? `${decodedGrade} · ` : ''}
          Complete each skill by answering all questions correctly.
        </p>
      </div>

      {!loading && !error && summary.total > 0 && (
        <div className="path-overview">
          <div className="path-overview-text">
            <span className="path-overview-count">
              {summary.completed} of {summary.total} skills completed
            </span>
            {nextSkill && (
              <span className="path-overview-hint">
                Up next: {nextSkill.name || nextSkill.subTopicName}
              </span>
            )}
          </div>
          <div className="progress-track path-overview-track">
            <span className="progress-fill" style={{ width: `${summary.percentage}%` }} />
          </div>
          {nextSkill && (
            <button
              type="button"
              className="primary path-overview-cta"
              onClick={() => startSkill(nextSkill)}
            >
              {summary.started ? 'Continue practicing' : 'Start practicing'}
            </button>
          )}
        </div>
      )}

      {loading && (
        <div className="browse-loading">
          <div className="browse-loading-spinner" />
          <span>Loading skills…</span>
        </div>
      )}

      {error && <div className="error">{error}</div>}

      {!loading && !error && !majorTopic && (
        <div className="browse-empty">
          No topic named “{decodedMajorTopic}” was found in {decodedGrade}.
        </div>
      )}

      {!loading && !error && majorTopic && (
        <div className="skill-group-list">
          {(majorTopic.subTopics || []).map((subTopic, index) => {
            const goalSections = getGoalSections(subTopic);
            if (!goalSections.length) return null;
            const subTopicNumber = index + 1;

            return (
              <section key={subTopic.name} className="skill-group">
                <header className="skill-group-header">
                  <span className="skill-group-number">{subTopicNumber}</span>
                  <h2 className="skill-group-title">{subTopic.name}</h2>
                </header>

                <div className="skill-list">
                  {goalSections.map((goalSection) => {
                    const stats = skillProgress(goalSection, standardStats);
                    const marker = goalSection.marker
                      ? formatGoalMarker(subTopicNumber, goalSection)
                      : '';

                    return (
                      <button
                        key={goalSection.code || goalSection.name}
                        type="button"
                        className={`skill-row skill-row-${skillStatusTone(stats)}`}
                        onClick={() => startSkill({ ...goalSection, subTopicName: subTopic.name })}
                        disabled={!(goalSection.questionCount > 0)}
                        title={`${goalSection.code || goalSection.name} · ${goalSection.questionCount} questions available`}
                      >
                        <span className="skill-row-main">
                          <span className="skill-row-label">
                            {marker && <span className="skill-row-marker">{marker}</span>}
                            <span className="skill-row-name">{goalSection.name}</span>
                          </span>
                          <span className="skill-row-progress">
                            <span className="progress-track skill-row-track">
                              <span
                                className="progress-fill"
                                style={{ width: `${stats.percentage}%` }}
                              />
                            </span>
                            <span className="skill-row-count">
                              {stats.correct}/{stats.target} correct
                            </span>
                          </span>
                        </span>
                        <span className={`skill-status skill-status-${skillStatusTone(stats)}`}>
                          {skillStatusLabel(stats)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default MajorTopicPage;
